/** Gradient surfaces that sample a shared button-sized light-leak canvas. */
export const BUTTON_LIGHT_LEAK_SURFACE_SELECTOR =
  '[data-slot="button-gradient-label"], [data-slot="button-gradient-arrow-circle"], [data-slot="button-gradient-arrow-icon"]';

/** Rive pseudo-3d ellipse tracks toward the cursor with partial offset damping. */
export const BACKGROUND_SHADOW_TRACKING_DAMPING = 0.32;

export function syncButtonLightLeakSurfaces(root: HTMLElement) {
  const rootRect = root.getBoundingClientRect();
  if (rootRect.width <= 0 || rootRect.height <= 0) return;

  root.style.setProperty("--button-light-canvas-w", `${rootRect.width}px`);
  root.style.setProperty("--button-light-canvas-h", `${rootRect.height}px`);

  root.querySelectorAll(BUTTON_LIGHT_LEAK_SURFACE_SELECTOR).forEach((node) => {
    const surface = node as HTMLElement;
    const surfaceRect = surface.getBoundingClientRect();

    surface.style.setProperty(
      "--button-light-offset-x",
      `${-(surfaceRect.left - rootRect.left)}px`
    );
    surface.style.setProperty(
      "--button-light-offset-y",
      `${-(surfaceRect.top - rootRect.top)}px`
    );
  });
}

export function updateLightLeakPosition(
  element: HTMLElement,
  clientX: number,
  clientY?: number
) {
  const { left, top, width, height } = element.getBoundingClientRect();
  if (width <= 0 || height <= 0) return;

  const xPercentage = Math.min(
    100,
    Math.max(0, ((clientX - left) / width) * 100)
  );
  element.style.setProperty(
    "--button-light-x",
    `${Math.round(xPercentage * 100) / 100}%`
  );

  if (clientY !== undefined) {
    const yPercentage = Math.min(
      100,
      Math.max(0, ((clientY - top) / height) * 100)
    );
    element.style.setProperty(
      "--button-light-y",
      `${Math.round(yPercentage * 100) / 100}%`
    );
  }

  syncButtonLightLeakSurfaces(element);
}

/** Reset background-shadow glow to the button center on pointer leave. */
export function resetBackgroundShadowPosition(element: HTMLElement) {
  element.style.setProperty("--button-light-x", "50%");
  element.style.setProperty("--button-light-y", "50%");
  element.style.setProperty("--button-light-shadow-x", "0px");
  element.style.setProperty("--button-light-shadow-y", "0px");
}

/**
 * Pointer tracking for the background-shadow variant. Matches the Rive pseudo-3d
 * ellipse model: a centered radial glow translated toward the cursor with damping
 * so the hotspot sits on the hovered edge rather than jumping to raw pointer %.
 */
export function updateBackgroundShadowPosition(
  element: HTMLElement,
  clientX: number,
  clientY: number,
  damping = BACKGROUND_SHADOW_TRACKING_DAMPING
) {
  const { left, top, width, height } = element.getBoundingClientRect();
  if (width <= 0 || height <= 0) return;

  const centerX = left + width / 2;
  const centerY = top + height / 2;
  const offsetX = (clientX - centerX) * damping;
  const offsetY = (clientY - centerY) * damping;

  const xPercentage = Math.min(
    100,
    Math.max(0, ((clientX - left) / width) * 100)
  );
  const yPercentage = Math.min(
    100,
    Math.max(0, ((clientY - top) / height) * 100)
  );

  element.style.setProperty(
    "--button-light-x",
    `${Math.round(xPercentage * 100) / 100}%`
  );
  element.style.setProperty(
    "--button-light-y",
    `${Math.round(yPercentage * 100) / 100}%`
  );
  element.style.setProperty(
    "--button-light-shadow-x",
    `${Math.round(offsetX * 100) / 100}px`
  );
  element.style.setProperty(
    "--button-light-shadow-y",
    `${Math.round(offsetY * 100) / 100}px`
  );
}
